"use client";

import ProductPage from "@/components/ProductPage";
import { KassensystemHardwareSection } from "@/components/KassensystemHardwareSection";

import ImageHighlight from "./highlight.jpg";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import { Check } from "lucide-react";
import { PricingCard } from "@/components/PricingSection";
import Image from "next/image";

export default function Page() {
  return (
    <ProductPage
      productName="Cashtex Profi – Kassensystem für Friseursalons & Kosmetikstudios"
      hero={{
        title:
          "Cashtex Profi – Kassensystem für Friseursalons & Kosmetikstudios",
        subtitle:
          "Speziell für Salons entwickelt: Terminplanung, Kundenkartei (inkl. Vorher-/Nachher-Notizen), reibungslose Zahlungs- und Serviceprozesse – alles in einem System.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHighlight,
        imageAlt: "Cashtex Profi Kassensystem für Friseure und Kosmetik",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Termin- & Anprobe-Planung</strong> inkl. Erinnerungen,
            Notizen und Abholterminen.
          </>,
          <>
            <strong>Anzahlungen & Reservierungen</strong> sauber verwalten –
            ideal für Braut- und Festmode.
          </>,
          <>
            <strong>Artikelvarianten & Anpassungen</strong> (Größen, Farben,
            Kollektionen, Änderungen) übersichtlich im System.
          </>,
          <>
            <strong>100 % rechtssicher</strong> –{" "}
            <a
              href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              GoBD
            </a>
            /
            <a
              href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              GDPdU
            </a>
            -konform inkl. Kassenbuch und{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            /
            <a
              href="https://www.softguide.de/funktion/fibu-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              FIBU-Export
            </a>
            /{" "}
            <a
              href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              EDI
            </a>
            -Schnittstelle/
            <a
              href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              TSE
            </a>
            -konform/Schnittstelle zu Ihrem Steuerberater.
          </>,
          <>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </>,
        ],
      }}
      beforeVideo={
        <div>
          <Section variant="transparent" className="py-4 md:py-6 mt-12">
            <ImageText
              imagePosition="right"
              imageSlot={
                <div className="relative overflow-hidden mt-15 rounded-2xl border border-black/10 bg-black shadow-sm h-100">
                  <video className="h-full w-full object-cover" controls>
                    <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                  </video>
                </div>
              }
            >
              <div className="flex flex-col gap-2 mb-2">
                <Image
                  src="/CTXr.svg"
                  alt="Cashtex"
                  width={120}
                  height={36}
                  className="object-contain"
                />
                <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                  Kassensystem für Friseure & Kosmetikstudios
                </h2>
              </div>

              <p className="text-slate-700 leading-relaxed">
                Cashtex unterstützt Friseursalons und Kosmetikstudios bei der
                Verwaltung von Terminen, Kunden und Serviceleistungen – einfach,
                übersichtlich und effizient.
              </p>

              <p className="mt-2 text-slate-700 leading-relaxed">
                Mit integrierter Kundenhistorie, flexiblen Zahlungsprozessen und
                Auswertungen behalten Sie jederzeit den Überblick über Ihren
                Salon.
              </p>

              <ul className="mt-2 space-y-2">
                <li className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 text-brand-primary" size={18} />
                  <span>
                    <strong>Terminplanung:</strong> Behandlungen und Zeiten
                    einfach organisieren und verwalten.
                  </span>
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 text-brand-primary" size={18} />
                  <span>
                    <strong>Kundenbindung:</strong> Historien, Lieblingsservices
                    und Aktionen zentral steuern.
                  </span>
                </li>

                <li className="flex items-start gap-3 text-slate-700">
                  <Check className="mt-1 text-brand-primary" size={18} />
                  <span>
                    <strong>Auswertungen:</strong> Umsätze und Leistungen
                    jederzeit im Blick behalten.
                  </span>
                </li>
              </ul>
            </ImageText>
          </Section>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Friseur & Kosmetik",
                standard: [
                  "Kassensystem & Verkauf",
                  "Kundenverwaltung",
                  "Produktverkauf",
                  "Berichte & Auswertungen",
                  "Zahlungsarten & Belegausgabe",
                  "Rabatte & Aktionspreise",
                  "Tagesabschluss",
                  "Mehrwertsteuer-Verwaltung",
                ],
                special: [
                  "Terminverwaltung ohne externe Tools oder Kalender",
                  "Mitarbeiterzuweisung für jeden Termin und jede Leistung",
                  "Dienstleistungen und Produkte in einem Vorgang kombinieren",
                  "Trinkgeld und Provisionen automatisch berücksichtigt",
                  "Kundenkartei mit Behandlungshistorie und Vorlieben",
                  "Umsatzauswertung pro Mitarbeiter und Leistungsart",
                  "Gutscheinverkauf und -einlösung direkt an der Kasse",
                  "Erinnerungen und Wiedervorlagen für Stammkunden",
                ],
              },
            ]}
          />
        </div>
      }
      faqs={[
        {
          q: "Kann Cashtex Termine und Behandlungen verwalten?",
          a: "Ja, Sie können Behandlungstermine inklusive individueller Services und optionaler Erinnerungen direkt in der Software anlegen und verwalten – ohne externe Kalender oder Tools.",
        },
        {
          q: "Ist Kunden- und Mitarbeiter-Management integriert?",
          a: "Ja. Cashtex bietet Kundenkartei, Auswertungen nach Mitarbeitenden sowie Marketing-Funktionen wie z. B. Geburtstagsaktionen – alles zentral in einem System.",
        },
        {
          q: "Unterstützt Cashtex Zahlungsprozesse flexibel?",
          a: "Ja. Von Belegen über Gutscheine bis zu Teilzahlungen – alle gängigen Zahlungsarten sind übersichtlich integriert und lassen sich kombinieren.",
        },
        {
          q: "Welche Auswertungen sind verfügbar?",
          a: "Umsatzübersichten, Service-Analysen, Renner-/Penner-Listen sowie Mitarbeitenden-Auswertungen – alles in Echtzeit abrufbar und exportierbar für Steuerberater und Buchhaltung.",
        },
        {
          q: "Wie starte ich mit der Demoversion?",
          a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
        },
        {
          q: "Kann ich Provisionen und Trinkgelder für Mitarbeitende abrechnen?",
          a: "Ja, Cashtex berücksichtigt Trinkgelder und Provisionen automatisch – für eine transparente und faire Abrechnung pro Mitarbeitendem.",
        },
        {
          q: "Lassen sich Produkte und Dienstleistungen kombiniert kassieren?",
          a: "Ja, Dienstleistungen und Produkte können in einem Kassenvorgang kombiniert werden – ideal für Salons, die neben Behandlungen auch Pflegeprodukte verkaufen.",
        },
        {
          q: "Wie läuft die Einrichtung und Schulung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeaturePOS,
          alt: "Cashtex Friseur – Verkauf und Kasse",
          title: "Schneller Verkauf & Kassenprozess",
          desc: "Rechnungen, Trinkgeld, Produktverkäufe und Belegdruck – ideal für Friseursalons und Kosmetikstudios.",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Cashtex Friseur – Kundenverwaltung",
          title: "Kundenverwaltung",
          desc: "Kundenhistorie, Lieblingsservices, Bonusprogramme und Terminerinnerungen – für zufriedene Stammkunden.",
        },
        {
          src: ImageFeatureStock,
          alt: "Cashtex Friseur – Artikel- und Lagerverwaltung",
          title: "Artikel- & Lagerverwaltung",
          desc: "Pflegeprodukte, Farben und Zubehör verwalten – mit Barcode-/Etikettendruck und Scanner-Inventur.",
        },
        {
          src: ImageFeatureStats,
          alt: "Cashtex Friseur – Statistiken",
          title: "Verkaufsstatistiken",
          desc: "Umsätze pro Mitarbeitenden, Dienstleistung und Produkt auswerten – klare Analysen für mehr Effizienz im Salon.",
        },
        {
          src: ImageFeatureOrder,
          alt: "Cashtex Friseur – Bestellwesen",
          title: "Bestellwesen & Wareneingang",
          desc: "Lieferantenbestellungen für Farben, Shampoos & Co., Wareneingang per Scanner und Übersicht offener Bestellungen.",
        },
        {
          src: ImageFeatureExport,
          alt: "Cashtex Friseur – Kassenbuch und Export",
          title: "Kassenbuch & Export",
          desc: "Einnahmen/Ausgaben, Trinkgeldverwaltung und Exporte für DATEV, Lexware und FIBU – weniger Aufwand in der Buchhaltung.",
        },
      ]}
    >
      {/* PRICING CARDS */}
      <Section className="pb-24 bg-white border-y border-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
          <PricingCard
            title="Leasing ab"
            priceFull="24"
            priceCents="90"
            details="Basierend auf 0,83 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Angebote, Order & Rechnungen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Mieten ab"
            priceFull="48"
            priceCents="90"
            details="Basierend auf 1,63 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Ideal für wachsende Unternehmen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Ratenkauf ab"
            priceFull="74"
            priceCents="90"
            details="Basierend auf 2,49 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Eigentumserwerb nach Laufzeit",
              "1 Benutzer",
            ]}
          />
        </div>

        <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
          Alle Preise exkl. MwSt.
        </div>
      </Section>
      <KassensystemHardwareSection />
      <Section variant="gray1" className="tse-section">
        <ImageText
          imagePosition="left"
          imageSrc="/tse.png"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary " />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>
    </ProductPage>
  );
}
